<template>
  <div class="lesson-sets">
    <div>
      <h4 class="review-title" @click="viewForm">
        {{ $t("homework.assignment_information") }}
        <i
          class="ml-px-16"
          v-bind:class="viewAssignmentDetail ? 'elsa-chevron-circle-up' : 'elsa-chevron-circle-down'"
        />
      </h4>
    </div>
    <assignment-detail
      ref="assignment_detail"
      class="assignment-detail"
      v-if="viewAssignmentDetail"
      key="assignmentReviewDetail"
    />
    <div>
      <h4 class="review-title" @click="viewLesson">
        {{ $t("homework.lesson_sets") }}
        <i class="ml-px-16" v-bind:class="viewLessonSets ? 'elsa-chevron-circle-up' : 'elsa-chevron-circle-down'" />
      </h4>
    </div>
    <elsa-tree
      v-if="viewLessonSets"
      accordion
      icon-class="tree-icon"
      :data="treeData"
      :form="form"
      :selected-items="selectedItems"
      v-on:node-click="handleNodeClick"
      v-on:add-remove="handleAddRemove"
      default-expand-all
    >
      <template v-slot:title="{ node }">
        <span v-bind:class="node.data.options.titleClassArray">{{ node.label }}</span>
      </template>
      <template v-slot:subtitle="{ node }">
        <span>{{ $t("homework.assignment_review.levels", [node.data.children.length]) }}</span>
      </template>
      <template v-slot:tags="{ node }">
        <span class="tag">{{ node.data.difficulty_level }}</span>
      </template>
      <template v-slot:tag-platform-only="{ node }">
        <span class="tag platform-only">{{ node.data.options.platformAvailability }} {{ $t("common.only") }}</span>
      </template>
    </elsa-tree>
  </div>
</template>

<script>
import ElsaTree from "@/components/common/Tree";
import { sortBy, sortWith, ascend, prop } from "ramda";
import AssignmentDetail from "@/components/homeworks/create-assignments/AssignmentCreateDetail";
import { getGameTypeIcon } from "@/utils/gameIconFactory";

export default {
  components: {
    ElsaTree,
    AssignmentDetail,
  },
  props: {
    dataSkill: {
      type: Array,
      default: () => [],
    },
    dataTopic: {
      type: Array,
      default: () => [],
    },
    dataBook: {
      type: Array,
      default: () => [],
    },
    lessonSelected: {
      type: Array,
      default: () => [],
    },
    lessonSelectedTopic: {
      type: Array,
      default: () => [],
    },
    lessonSelectedBook: {
      type: Array,
      default: () => [],
    },
    currentStep: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      defaultProps: {
        children: "children",
        label: "label",
      },
      form: {
        filterValue: undefined,
      },
      treeData: [],
      submoduleSelected: [],
      viewAssignmentDetail: true,
      viewLessonSets: true,
      moduleName: {
        skill: "topic_name",
        topic: "theme_name",
      },
    };
  },
  mounted() {
    this.parseData();
  },
  computed: {
    selectedItems() {
      return {
        "level-3": this.submoduleSelected,
        "level-4": this.lessonSelected,
      };
    },
    lessonSelectedSkills() {
      return this.lessonSelected.filter(x => !this.lessonSelectedTopic.includes(x));
    },
  },
  methods: {
    parseData() {
      const tree = [];
      let order = 1;
      this.dataSkill.map(row => {
        const root = {
          id: row.id,
          label: this.$t("homework.assignment_review.skill_label") + ' ' + row.name,
          children: [],
          original_id: row.original_id,
          order: order,
          original_order: row.order,
          options: {
            showIcon: true,
            iconClassArray: ["elsa-grip-lines"],
            titleClassArray: ["level-title", "root-title"],
          },
          root_type: "skill",
        };
        root.children = this.appendModule(row.modules, "skill");
        if (root.children.length > 0) {
          tree.push(root);
          order++;
        }
      });
      this.dataTopic.map(row => {
        const root = {
          id: row.id,
          label: this.$t("homework.assignment_review.topic_label") + ' ' + row.name,
          children: [],
          original_id: row.original_id,
          order: order,
          original_order: row.order,
          options: {
            showIcon: true,
            iconClassArray: ["elsa-grip-lines"],
            titleClassArray: ["level-title", "root-title"],
          },
          root_type: "topic",
        };
        root.children = this.appendModule(row.modules, "topic");
        if (root.children.length > 0) {
          tree.push(root);
          order++;
        }
      });
      if (this.dataBook.length > 0) {
        const root = {
          id: order,
          label: 'Premium Content',
          children: [],
          order: order,
          options: {
            showIcon: true,
            iconClassArray: ["elsa-grip-lines"],
            titleClassArray: ["level-title", "root-title"],
          },
          root_type: "book",
        };
        root.children = this.appendBook();
        if (root.children.length > 0) {
          tree.push(root);
          order++;
        }
      }
      this.treeData = tree;
    },
    appendModule(modules, type = "skill") {
      modules = sortBy(ascend(prop("order")))(modules);
      const topics = [];
      const moduleNameAttr = this.moduleName[type];
      modules.map(module => {
        const topic = {
          id: module.id,
          label: module[moduleNameAttr],
          children: [],
          original_id: module.topic_original_id,
          options: {
            showIcon: true,
            iconClassArray: ["elsa-grip-lines"],
            titleClassArray: ["level-title", "level-2-title"],
          },
        };
        topic.children = this.appendSubmodule(module.submodules, type, module.platform_availability);
        if (topic.children.length > 0) {
          topics.push(topic);
        }
      });
      return topics;
    },
    appendSubmodule(submodules, type = "skill", platform_availability) {
      submodules = sortBy(ascend(prop("order")))(submodules);
      const children = [];
      let order = 1;
      submodules.map(submodule => {
        const prefix = this.$t("homework.assignment_review.level", [order]) + ' ';
        const newChild = {
          id: submodule.id,
          label: prefix + submodule.name,
          children: [],
          order: order,
          original_order: submodule.order,
          original_id: submodule.original_id,
          children_original_id: [],
          options: {
            showListAction: true,
            hideAddAction: true,
            removeNode: true,
            showIcon: true,
            iconClassArray: ["elsa-grip-lines"],
            titleClassArray: ["level-title", "level-3-title"],
            removeActionText: this.$t("homework.assignment_review.remove_all"),
            showConfirm: true,
            platformAvailability: platform_availability ? platform_availability.toLowerCase() : null,
          },
        };
        let lessons = sortWith([ascend(prop("order"))])(submodule.lessons);
        lessons.map(lesson => {
          const newSubChild = {
            id: lesson.id,
            label: `${lesson.name} - ${lesson.title}`,
            children: [],
            difficulty_level: lesson.difficulty_level,
            game_type: lesson.game_type,
            original_id: lesson.original_id,
            submodule_id: submodule.original_id,
            original_order: lesson.order,
            options: {
              showListAction: true,
              removeNode: true,
              showTags: true,
              showIcon: true,
              iconClassArray: [getGameTypeIcon(lesson.game_type), "game-type"],
              titleClassArray: ["level-title", "level-4-title"],
              showConfirm: true,
            },
          };

          let isSelected = false;
          let canAdd = false;
          if (
            (type === "skill" && this.lessonSelectedSkills.includes(lesson.original_id)) ||
            (type === "topic" && this.lessonSelectedTopic.includes(lesson.original_id))
          ) {
            canAdd = true;
          }
          if (
            this.lessonSelected.length === 0 ||
            (this.lessonSelected.length > 0 && this.lessonSelected.includes(lesson.original_id) && canAdd)
          ) {
            isSelected = true;
            this.handelAddSubModule(submodule.original_id);
          }

          if (isSelected) {
            newChild.children.push(newSubChild);
            newChild.children_original_id.push(lesson.original_id);
          }
        });
        if (newChild.children.length > 0) {
          children.push(newChild);
          order++;
        }
      });
      return children;
    },
    appendBook() {
      let books = [];
      let order = 1;
      this.dataBook.map(row => {
        const book = {
          id: row.id,
          label: row.name,
          children: [],
          original_id: row.original_id,
          order: order,
          options: {
            showIcon: true,
            iconClassArray: ["elsa-grip-lines"],
            titleClassArray: ["level-title", "level-2-title"],
          },
          root_type: "book",
        };
        const modules = sortBy(ascend(prop("order")))(row.modules);
        modules.map(module => {
          let subOrder = 1;
          const topic = {
            id: module.id,
            label: module.name,
            children: [],
            order: subOrder,
            original_order: module.order,
            original_id: module.original_id,
            children_original_id: [],
            options: {
              showListAction: true,
              hideAddAction: true,
              removeNode: true,
              showIcon: true,
              iconClassArray: ["elsa-grip-lines"],
              titleClassArray: ["level-title", "level-3-title"],
              removeActionText: this.$t("homework.assignment_review.remove_all"),
              showConfirm: true,
              platformAvailability: module.platform_availability ? module.platform_availability.toLowerCase() : null,
            },
          };
          let lessons = sortWith([ascend(prop("order"))])(module.lessons);
          lessons.map(lesson => {
            const newChild = {
              id: lesson.id,
              label: `${lesson.name} - ${lesson.title}`,
              children: [],
              difficulty_level: lesson.difficulty_level,
              game_type: lesson.game_type,
              original_id: lesson.original_id,
              original_order: lesson.order,
              options: {
                showListAction: true,
                removeNode: true,
                showTags: true,
                showIcon: true,
                iconClassArray: [getGameTypeIcon(lesson.game_type), "game-type"],
                titleClassArray: ["level-title", "level-4-title"],
                showConfirm: true,
              },
            };

            let isSelected = false;
            let canAdd = false;
            if (this.lessonSelectedBook.includes(lesson.original_id)) {
              canAdd = true;
            }

            if (
              this.lessonSelected.length === 0 ||
              (this.lessonSelected.length > 0 && this.lessonSelected.includes(lesson.original_id) && canAdd)
            ) {
              isSelected = true;
              this.handelAddSubModule(module.original_id);
            }

            if (isSelected) {
              topic.children.push(newChild);
              topic.children_original_id.push(lesson.original_id);
            }
          });
          if (topic.children.length > 0) {
            book.children.push(topic);
            subOrder++;
          }
        });
        if (book.children.length > 0) {
          books.push(book);
          order++;
        }
      });
      return books;
    },
    getTitleByLevel(node) {
      let prefix = "";
      if (node.level === 1) {
        prefix = this.$t("homework.assignment_review.skill_label");
        if (node.data.root_type === "topic") {
          prefix = this.$t("homework.assignment_review.topic_label");
        }
        if (node.data.root_type === "book") {
          prefix = "";
        }
      }
      if (node.level === 3) prefix = this.$t("homework.assignment_review.level", [node.data.order]);
      return prefix + " " + node.label;
    },
    handleAddRemove(node) {
      if (node.level === 1) {
        this.$emit("remove-lesson", node.data.children_original_id);
      } else if (node.level === 2) {
        this.$emit("remove-lesson", [node.data.original_id]);
      } else if (node.level === 3) {
        this.handelRemoveSubModule(node.data);
      } else if (node.level === 4) {
        this.handelRemoveLesson(node.data, node.parent.data);
      }
    },
    handelAddSubModule(submodule) {
      if (!this.submoduleSelected.includes(submodule)) this.submoduleSelected.push(submodule);
    },
    handelRemoveSubModule(submodule) {
      // console.info('handelRemoveSubModule', submodule.original_id, submodule.children_original_id)
      let index = this.submoduleSelected.indexOf(submodule.original_id);
      if (index > -1) this.submoduleSelected.splice(index, 1);

      this.$emit("remove-lesson", submodule.children_original_id);
      this.$emit("remove-submodule", submodule.original_id);
    },
    async handelRemoveLesson(lesson, parent) {
      // console.info('handelRemoveLesson', parent.original_id, lesson.original_id)
      await this.$emit("remove-lesson", [lesson.original_id]);
      let is_subset = !parent.children_original_id.some(val => this.lessonSelected.indexOf(val) === -1);
      if (!is_subset) await this.$emit("remove-submodule", parent.original_id);
    },
    handleNodeClick(data, node) {
      // console.info("handleNodeClick", node.level, node.data.original_id);
    },
    createAssignment() {
      this.$emit("next-step", this.currentStep);
    },
    viewForm() {
      this.viewAssignmentDetail = !this.viewAssignmentDetail;
      this.viewLessonSets = false;
    },
    viewLesson() {
      this.viewAssignmentDetail = false;
      this.viewLessonSets = !this.viewLessonSets;
    },
  },
};
</script>

<style lang="scss" scoped>
.lesson-sets {
  border: 1px solid #efeff4;
  margin-bottom: 40px;
  /*border-radius: 12px;*/
}
.review-title {
  font-size: 20px;
  line-height: 20px;
  font-weight: 600;
  text-transform: uppercase;
  padding: 32px;
  margin: 0;
  border-bottom: 1px solid #efeff4;
}
.elsa-chevron-circle-down,
.elsa-chevron-circle-up {
  display: inline-block;
  margin-bottom: -4px;
}
.tag {
  padding: 4px 8px;
  background: #efeff4;
  border-radius: 4px;
  text-transform: uppercase;
  margin-top: 4px;
  font-weight: 600;
  font-size: 12px;
  line-height: 14px;
  letter-spacing: 0.25px;
  font-variant: small-caps;
}
.platform-only {
  background: rgba(0, 126, 255, 0.1) !important;
  font-family: SF Pro Display, sans-serif;
  font-style: normal;
  font-weight: 700;
  color: #007eff;
  margin-top: 0px;
}
.root-title {
  font-weight: 600;
  font-size: 20px;
}

.level-title {
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #121131;
  font-family: sf-pro-text, sans-serif;
}

.level-1-title {
  font-weight: 500;
  font-size: 20px;
}

.level-2-title {
  font-size: 16px;
}

.level-3-title {
  font-size: 14px;
  line-height: 24px;
}

.level-4-title {
  font-size: 14px;
  line-height: 24px;
  font-weight: 400;
}

::v-deep .base-create {
  margin: 0px;
  padding: 32px;
  background: linear-gradient(0deg, rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.7)), #efeff4;
  padding-bottom: 0;
}

::v-deep .create-edit-form {
  padding-bottom: 0;
  width: 680px;
}

::v-deep .el-form {
  padding-bottom: 0;
}

::v-deep .el-tree-node__content {
  padding: 16px 32px;
}

::v-deep .el-tree {
  border-radius: unset;
  border: unset;
}
</style>
